import ky, { HTTPError } from 'ky'

declare global {
  interface Window {
    env?: {
      apiUrl: string
    }
  }
}

// MODE (not import.meta.env.DEV): the dev API proxy is for the local dev server
// only; unit tests run in Vite mode 'test' and keep the direct-API path.
const isDev = import.meta.env.MODE === 'development'
const useProxyPrefixPath =
  import.meta.env.VITE_USE_CAPTURI_DEV_PROXY === 'true' || isDev

const remoteOrigin = window.env?.apiUrl || 'https://api.capturi.ai'
const prefixUrl = `${useProxyPrefixPath ? '/api' : remoteOrigin}/authentication`

const createProviderUrl = (
  provider: string,
  params: URLSearchParams,
): string => {
  return useProxyPrefixPath
    ? `/authentication/${provider}?${params}`
    : `${remoteOrigin}/authentication/${provider}?${params}`
}

const api = ky.create({
  prefixUrl,
  credentials: 'include',
  retry: 0,
})

type RefreshTokenResponseModel = {
  ttl: number
  org: string
}

export const shouldBypassAuth =
  new URLSearchParams(window.location.search).get('bypass_auth') != null

class AuthService {
  #pendingAccessTokenPromise?: Promise<void>
  #expireTime?: number
  currentOrganization?: string
  listOrganizationsURL = 'authentication/me/organizations'

  get pendingAccessTokenPromise(): Promise<void> | undefined {
    return this.#pendingAccessTokenPromise
  }

  isRefreshUrl(url: string): boolean {
    return url.includes(`${prefixUrl}/refresh`)
  }

  validateAuthentication(): Promise<void> | void {
    if (this.#expireTime && this.#expireTime > Date.now()) return

    return this.refreshAccessToken()
  }

  async changeOrganization(
    organizationUid: string,
    config?: { dontGoToOrg: boolean },
  ): Promise<void> {
    await api.post('me/change-org', { json: { uid: organizationUid } })

    if (!(config?.dontGoToOrg === true)) {
      window.location.href = window.location.origin
    }
  }

  refreshAccessToken(): Promise<void> | undefined {
    if (shouldBypassAuth) return
    if (this.#pendingAccessTokenPromise) {
      return this.#pendingAccessTokenPromise
    }

    this.#pendingAccessTokenPromise = api
      .get('refresh')
      .json<RefreshTokenResponseModel>()
      .then((resp) => {
        this.#expireTime = Date.now() + resp.ttl
        this.currentOrganization = resp.org
      })
      .catch((e) => {
        if (e.response?.status === 401) throw e
      })
      .finally(() => {
        this.#pendingAccessTokenPromise = undefined
      })
    return this.#pendingAccessTokenPromise
  }

  async signOut(): Promise<void> {
    try {
      await api.post('sign-out')
    } catch {
      // doesn't matter
    } finally {
      window.location.href = window.location.origin
    }
  }

  addOrganization = async (
    invite: string,
    language: string,
  ): Promise<{ org: string }> => {
    try {
      return await api
        .post('me/organizations', {
          json: {
            invite,
            language,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        })
        .json<{ org: string }>()
    } catch (error) {
      let e = error
      try {
        if (error instanceof HTTPError) {
          e = await error.response.json()
        }
      } catch {
        throw e
      }
      throw e
    }
  }

  generateAuthURL = (provider: string, language: string): string => {
    const params = new URLSearchParams(window.location.search)

    const invite = params.get('invite')
    params.delete('invite') //Remove invite from the return url
    const org = params.get('org')
    params.delete('org') //Remove org from the return url

    const { pathname, origin } = window.location
    //Generate the return url
    const returnUrl = encodeURI(`${origin}${pathname}?${params.toString()}`)

    //Create the return url param
    const newParams = new URLSearchParams()
    if (org) newParams.append('org', org)
    newParams.append('return_url', returnUrl)
    newParams.append('language', language)
    newParams.append(
      'time_zone',
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    )
    if (invite) newParams.append('invite', invite) //Add the invite param if it exist

    return createProviderUrl(provider, newParams)
  }
}

export default new AuthService()
